import { useEffect, useState, useMemo } from 'react';
import { FEEDING_STATUS, FEEDING_THRESHOLDS, DEFAULT_FEEDING_INTERVAL_HOURS } from '../config/constants';

/**
 * Convierte un Timestamp de Firestore, un Date o null a milisegundos epoch.
 * Tolera los distintos formatos que puede tomar lastFed según contexto:
 *  - Firestore Timestamp (server) → .toMillis()
 *  - Date (cliente, antes de sync) → .getTime()
 *  - null → null
 */
const toMillis = (value) => {
  if (!value) return null;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number') return value;
  return null;
};

/**
 * Calcula el estado actual del semáforo dada la última alimentación,
 * el intervalo configurado y el "ahora" actual.
 *
 * Exportada para que sea testeable sin renderizar el hook.
 */
export const computeFeedingStatus = ({ lastFedMs, intervalHours, nowMs }) => {
  if (!lastFedMs) {
    return {
      status: FEEDING_STATUS.UNKNOWN,
      elapsedMs: null,
      intervalMs: intervalHours * 3_600_000,
      fractionElapsed: null,
      hoursUntilNext: null,
    };
  }

  const intervalMs = intervalHours * 3_600_000;
  const elapsedMs = Math.max(0, nowMs - lastFedMs);
  const fractionElapsed = elapsedMs / intervalMs;

  let status;
  if (fractionElapsed < FEEDING_THRESHOLDS.GREEN_FRACTION) {
    status = FEEDING_STATUS.GREEN;
  } else if (fractionElapsed < FEEDING_THRESHOLDS.YELLOW_FRACTION) {
    status = FEEDING_STATUS.YELLOW;
  } else {
    status = FEEDING_STATUS.RED;
  }

  const hoursUntilNext = Math.max(0, (intervalMs - elapsedMs) / 3_600_000);

  return {
    status,
    elapsedMs,
    intervalMs,
    fractionElapsed,
    hoursUntilNext,
  };
};

/**
 * Hook reactivo que recalcula el estado del semáforo cada minuto.
 *
 * @param {object} pet - Documento de mascota con lastFed y feedingIntervalHours
 * @param {object} [options]
 * @param {number} [options.refreshIntervalMs=60_000] - Cada cuánto recalcular
 *
 * @returns {object} { status, elapsedMs, intervalMs, fractionElapsed, hoursUntilNext, isRecentlyFed }
 */
export const useFeedingStatus = (pet, { refreshIntervalMs = 60_000 } = {}) => {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    // Sin pet o sin lastFed, no necesitamos refresh — el estado es estático.
    if (!pet?.lastFed) return;

    const id = setInterval(() => setNow(Date.now()), refreshIntervalMs);
    return () => clearInterval(id);
  }, [pet?.lastFed, refreshIntervalMs]);

  // useMemo evita recalcular cuando otras props del pet cambian (ej: name).
  return useMemo(() => {
    const lastFedMs = toMillis(pet?.lastFed);
    const intervalHours = pet?.feedingIntervalHours ?? DEFAULT_FEEDING_INTERVAL_HOURS;

    const result = computeFeedingStatus({
      lastFedMs,
      intervalHours,
      nowMs: now,
    });

    /**
     * isRecentlyFed: bandera UX para advertir "Alimentado hace poco".
     * Se considera "reciente" si pasó menos del 25% del intervalo.
     * En la UI, esto dispara el modal de confirmación "¿Seguro que querés alimentar de nuevo?".
     */
    const isRecentlyFed =
      result.fractionElapsed !== null && result.fractionElapsed < 0.25;

    return { ...result, isRecentlyFed };
  }, [pet?.lastFed, pet?.feedingIntervalHours, now]);
};